import type { ChangeEvent } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormReturn,
  Path,
  FieldErrors,
  PathValue,
  FieldError,
  useFormState,
} from 'react-hook-form';

import { capitalizeFirstLetter } from './strings';

/**
 * Serializes React Hook Form errors into a string for use in effect dependencies.
 * Avoids circular reference errors caused by DOM refs in the errors object.
 *
 * @param errors - The errors object from `formState.errors`
 * @returns A string representation of all error messages, or empty string if no errors
 */
export const getFormErrorsKey = <T extends Record<string, unknown>>(
  errors: FieldErrors<T>
): string =>
  Object.entries(errors)
    .map(([key, err]) => `${key}:${(err as FieldError | undefined)?.message ?? ''}`)
    .join('|');

/**
 * Every leaf error of a nested form as `questions.0.options.1.label` → message.
 * `getFormErrorsKey` only reads the top level; use this for field arrays.
 */
export const flattenFormErrors = (errors: FieldErrors | undefined): Record<string, string> => {
  const flat: Record<string, string> = {};
  const stack: { node: unknown; path: string }[] = [{ node: errors, path: '' }];
  while (stack.length > 0) {
    const { node, path } = stack.pop() as { node: unknown; path: string };
    if (!node || typeof node !== 'object') {
      continue;
    }
    const record = node as Record<string, unknown>;
    if (typeof record.message === 'string') {
      flat[path] = record.message;
      continue;
    }
    for (const [key, value] of Object.entries(record)) {
      // `ref` is the DOM element; `type`/`types` describe the rule, not a nested field
      if (key !== 'ref' && key !== 'type' && key !== 'types') {
        stack.push({ node: value, path: path ? `${path}.${key}` : key });
      }
    }
  }
  return flat;
};

/**
 * Middleware to trim form string inputs and execute submit or invalid validation routines.
 *
 * @param {UseFormReturn} formMethods - Methods to manipulate form state.
 * @param {(data: unknown) => void} onSubmit - Callback function to execute on valid submit.
 * @returns {Function} - Handler function to be used with form submission.
 */
export const submitWithTrimming = <FormType>(
  formMethods: UseFormReturn<FormType>,
  onSubmit: (data: unknown) => void
) => {
  const submitMiddleware = async (data) => {
    for (const [key] of Object.entries(data)) {
      if (typeof data[key] === 'string') {
        formMethods.setValue(
          key as Path<FormType>,
          data[key].trim() as PathValue<FormType, Path<FormType>>
        );
      }
    }
    const form = formMethods.getValues();
    onSubmit(form);
    formMethods.reset(form);
  };

  const onInvalidSubmit = async (errors: FieldErrors<FormType>) => {
    for (const [key] of Object.entries(errors)) {
      const value = formMethods.getValues(key as Path<FormType>);
      if (typeof value === 'string') {
        formMethods.setValue(
          key as Path<FormType>,
          value.trim() as PathValue<FormType, Path<FormType>>
        );
      }
    }
    formMethods.handleSubmit(submitMiddleware)();
  };
  return formMethods.handleSubmit(submitMiddleware, onInvalidSubmit);
};

/**
 * Formats error messages by capitalizing the first letter.
 * Designed to work with react-hook-form field errors.
 *
 * @param error - Error object with optional message property
 * @returns Formatted error message string with first letter capitalized, or empty string if no message
 *
 */
export const formatErrorMessage = (error: FieldError | undefined): string => {
  if (!error?.message) {
    return '';
  }
  return capitalizeFirstLetter(error.message);
};

/**
 * Checks if a form field has unsaved changes or not
 *
 * The boolean checker used to be `form.formState.isDirty || !isEmpty(form.formState.dirtyFields)`
 * However, form.formState.isDirty was firing true even when form.formState.dirtyFields was empty.
 * So the boolean is currently simplified to !isEmpty(form.formState.dirtyFields).
 *
 * @param form - form object
 * @returns boolean
 */
export const hasDirtyFields = (form): boolean => !isEmpty(form.formState.dirtyFields);

/**
 * `hasDirtyFields` for use during render. react-hook-form mutates `dirtyFields` in place, so the
 * React Compiler would cache anything computed from it; this recomputes on every render and
 * returns a boolean, which is safe to cache. Use `hasDirtyFields` in handlers.
 */
export const useHasDirtyFields = <T extends FieldValues>(control: Control<T>): boolean => {
  'use no memo';
  const { dirtyFields } = useFormState({ control });
  return !isEmpty(dirtyFields);
};

/**
 * Controller props for a number input that store what `register`'s `valueAsNumber` did: the
 * typed number, or NaN for an empty box, which the input shows as empty.
 */
export const numberFieldProps = <T extends FieldValues, N extends FieldPath<T>>(
  field: ControllerRenderProps<T, N>
) => ({
  ...field,
  value: Number.isNaN(field.value) ? '' : (field.value ?? ''),
  onChange: (event: ChangeEvent<HTMLInputElement>) => field.onChange(event.target.valueAsNumber),
});
